import { useMemo } from 'react';

import StarIcon from '@mui/icons-material/Star';
import { Typography, useTheme } from '@mui/material';

import { CartesianGrid, ResponsiveContainer, Scatter, ScatterChart, Tooltip, XAxis, YAxis } from 'recharts';
import styled from 'styled-components';

import { useFormatPercent } from '@arrived/hooks';
import { QuarterDividend } from '@arrived/models';

import { TextWrapper } from '../../../TextWrapper';

const DividendChartContainer = styled.div`
  &.dividend-chart-container {
    position: relative;

    .header-container {
      margin-bottom: ${({ theme }) => theme.spacing(1)};

      .chart-title {
        display: flex;
        justify-content: center;
      }

      .legend-container {
        position: absolute;
        right: 0;
        top: 0;
        display: flex;
        gap: ${({ theme }) => theme.spacing(1)};
        justify-items: center;
        margin-right: ${({ theme }) => theme.spacing(1)};
      }
    }

    .dividend-tooltip {
      background-color: ${({ theme }) => theme.palette.neutrals.white};
      box-shadow: 0px -4px 5px 2px rgba(0, 0, 0, 0.03), 0px 4px 5px 2px rgba(0, 0, 0, 0.03);
      padding: ${({ theme }) => theme.spacing(1)};
    }
  }
`;

const DividendTooltip = ({ active, payload }: any) => {
  const formatPercent = useFormatPercent();
  if (active && payload && payload.length) {
    return (
      <div className="dividend-tooltip">
        {/* Property name */}
        <Typography variant="body1">{payload[0].payload.property}</Typography>
        {/* Property dividend percentage */}
        <Typography variant="body1">{formatPercent(payload[1].value)}</Typography>
      </div>
    );
  }

  return null;
};

interface DividendChartProps {
  marketTitle: string;
  quarterlyDividends: QuarterDividend[];
}

export const DividendChart = ({ marketTitle, quarterlyDividends }: DividendChartProps) => {
  const formatPercent = useFormatPercent();
  const reversedQuarterlyDividends = useMemo(() => quarterlyDividends.reverse(), [quarterlyDividends]);
  const { palette } = useTheme();

  return (
    <DividendChartContainer className="dividend-chart-container">
      <div className="header-container">
        <Typography className="chart-title" variant="body1">
          {marketTitle}
        </Typography>
        <div className="legend-container">
          <StarIcon color="secondary" />
          <TextWrapper
            typographyProps={{ variant: 'body1' }}
            formattedMessageProps={{ id: 'chart-average', defaultMessage: 'Avg' }}
          />
        </div>
      </div>
      <ResponsiveContainer height={200}>
        <ScatterChart>
          <CartesianGrid stroke={palette.neutrals.platinum} />
          <XAxis dataKey="label" name="Label" allowDuplicatedCategory={false} />
          <YAxis
            dataKey="dividendRate"
            name="Dividend"
            tickFormatter={formatPercent}
            domain={['dataMin - 0.005', 'dataMax + 0.005']}
          />
          <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<DividendTooltip />} />
          {reversedQuarterlyDividends.map((group, idx) => (
            <Scatter
              key={`group-${idx}`}
              name="Dividends"
              data={group.dividends.map((propertyDividend) => ({
                label: group.label,
                dividendRate: propertyDividend.dividend,
                property: propertyDividend.label,
              }))}
              fill={palette.primary.main}
            />
          ))}
          {reversedQuarterlyDividends.map((group, idx) => (
            <Scatter
              key={`avg-${idx}`}
              name="Dividends"
              data={[{ label: group.label, dividendRate: group.averageDividend, property: group.label }]}
              shape="star"
              fill={palette.tertiary[500]}
            />
          ))}
        </ScatterChart>
      </ResponsiveContainer>
    </DividendChartContainer>
  );
};
